import React, { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from 'components/AppIcon';
import { cn } from 'utils/cn';

/**
 * Mobile-only Speed Dial FAB (portaled to document.body).
 * Portaled so `position: fixed` is not trapped by sticky/backdrop-filter ancestors.
 *
 * @param {{
 *   ariaLabel?: string;
 *   className?: string;
 *   visibilityClassName?: string;
 *   actions: Array<{
 *     id: string;
 *     label: string;
 *     iconName: string;
 *     onClick?: () => void;
 *     disabled?: boolean;
 *     variant?: 'default' | 'primary' | 'destructive';
 *   }>;
 * }} props
 */
const SpeedDialFab = ({
  ariaLabel = 'Ações',
  className,
  visibilityClassName = 'md:hidden',
  actions = [],
}) => {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const menuId = useId();
  const visibleActions = (actions ?? []).filter(Boolean);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const handleAction = (action) => {
    if (action.disabled) return;
    setOpen(false);
    action.onClick?.();
  };

  if (!visibleActions.length || !mounted) return null;

  return createPortal(
    <div
      className={cn(
        // pointer-events-none: closed action list must not steal taps from
        // other fixed controls (e.g. contract-generator "Ver Prévia").
        'fixed z-[1100] flex flex-col items-end pointer-events-none',
        'right-3 sm:right-4',
        'bottom-[calc(0.75rem+env(safe-area-inset-bottom,0px))]',
        visibilityClassName,
        className
      )}
    >
      {open ? (
        <button
          type="button"
          aria-label="Fechar ações"
          className="pointer-events-auto fixed inset-0 z-0 bg-slate-900/35 backdrop-blur-[1px] transition-opacity duration-150"
          onClick={() => setOpen(false)}
        />
      ) : null}

      <div
        id={menuId}
        role="menu"
        aria-hidden={!open}
        className={cn(
          // Absolute above the FAB so closed (opacity-0) actions do not expand
          // the wrapper hit-box over neighboring floating buttons.
          'absolute bottom-full right-0 mb-3 z-10 flex flex-col items-end gap-2.5 transition-all duration-200',
          open
            ? 'pointer-events-auto opacity-100 translate-y-0'
            : 'pointer-events-none opacity-0 translate-y-2'
        )}
      >
        {visibleActions.map((action, index) => {
          const isPrimary = action.variant === 'primary';
          const isDestructive = action.variant === 'destructive';

          return (
            <div
              key={action.id}
              className="flex items-center gap-2"
              style={{
                transitionDelay: open ? `${index * 35}ms` : '0ms',
              }}
            >
              <span
                className={cn(
                  'max-w-[11rem] truncate rounded-md border border-border bg-card px-2.5 py-1.5',
                  'text-xs font-medium text-foreground shadow-elevation-sm'
                )}
              >
                {action.label}
              </span>
              <button
                type="button"
                role="menuitem"
                disabled={action.disabled}
                aria-label={action.label}
                title={action.label}
                onClick={() => handleAction(action)}
                className={cn(
                  'inline-flex h-11 w-11 items-center justify-center rounded-full border shadow-elevation-md',
                  'transition-transform duration-150 active:scale-95',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  'disabled:pointer-events-none disabled:opacity-45',
                  isPrimary &&
                    'border-primary bg-primary text-primary-foreground hover:bg-[var(--color-primary-hover)]',
                  isDestructive &&
                    'border-destructive/40 bg-card text-destructive hover:bg-destructive/10',
                  !isPrimary &&
                    !isDestructive &&
                    'border-border bg-card text-foreground hover:bg-muted'
                )}
              >
                <Icon name={action.iconName} size={18} />
              </button>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        aria-label={open ? 'Fechar ações' : ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'pointer-events-auto relative z-10 inline-flex h-14 w-14 items-center justify-center rounded-full',
          'bg-primary text-primary-foreground shadow-elevation-lg',
          'transition-transform duration-200 hover:bg-[var(--color-primary-hover)] active:scale-95',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          open && 'scale-95'
        )}
      >
        <Icon
          name={open ? 'X' : 'Plus'}
          size={24}
          className={cn('transition-transform duration-200', open && 'rotate-90')}
        />
      </button>
    </div>,
    document.body
  );
};

export default SpeedDialFab;
